import React, { useRef } from 'react';
import ApiPost from '../../../../../../api-controller-mod/api-serv/post-api/postApi';
import { JoinUsSection, JoinUsCardSection, JoinUsStep, MediaSaveCallback } from './types';
import { LabelRow, SplitRow, DraftTextField, DraftTextArea } from './primitives';
import ImageUploadField from './imageUploadField';
import { API_ENDPOINTS } from '../../../../../../context-controller-mod/context-serv/global-provider/api-shell/api-provider';

interface JoinUsEditorProps {
  data:               JoinUsSection;
  onChange:           (v: JoinUsSection) => void;
  onMediaClick?:      (onSave: MediaSaveCallback) => void;
  userId:             string;
  registerMediaUsage: (uuid: string) => void;
}

const JoinUsEditor: React.FC<JoinUsEditorProps> = ({
  data, onChange, onMediaClick, userId, registerMediaUsage,
}) => {
  const cs   = data.content[0]?.cardSection;
  const orig = useRef(cs);

  const setCs = (p: Partial<JoinUsCardSection>) => onChange({
    ...data,
    content: [{ ...data.content[0], cardSection: { ...cs, ...p } }],
  });

  const setStep = (idx: number, p: Partial<JoinUsStep>) => {
    const updatedList = cs.list.map((s, i) => i === idx ? { ...s, ...p } : s);
    setCs({ list: updatedList });
  };

  if (!cs) return null;

  return (
    <>
      <LabelRow label="Main content title" />
      <SplitRow currentText={`[ ${orig.current?.label ?? ''} ]`}>
        <DraftTextField placeholder="Text title" onChange={v => setCs({ label: v })} />
      </SplitRow>

      <LabelRow label="Main title" />
      <SplitRow currentText={orig.current?.title ?? ''}>
        <DraftTextField placeholder="Main title" onChange={v => setCs({ title: v })} />
      </SplitRow>

      <LabelRow label="Main description" />
      <SplitRow currentText={orig.current?.description ?? ''} multiline>
        <DraftTextArea placeholder="Main description" rows={3}
          onChange={v => setCs({ description: v })} />
      </SplitRow>

      {orig.current?.list.map((step, idx) => (
        <React.Fragment key={step.id}>
          <LabelRow label={`Step ${idx + 1}`} />
          <SplitRow currentText={step.title}>
            <DraftTextField
              key={`step-title-${idx}`}
              initialValue={''}
              placeholder="Text title"
              onChange={v => setStep(idx, { title: v })} />
          </SplitRow>
          <SplitRow currentText={step.description} multiline>
            <DraftTextArea
              key={`step-desc-${idx}`}
              placeholder="Title description" rows={3}
              onChange={v => setStep(idx, { description: v })} />
          </SplitRow>

          {orig.current?.list[idx]?.button !== undefined && (
            <>
              <LabelRow label={`Step ${idx + 1} Button`} />
              <SplitRow currentText={orig.current.list[idx].button?.title ?? ''}>
                <DraftTextField
                  key={`step-btn-${idx}`}
                  initialValue={''}
                  placeholder="Button title"
                  onChange={v => setStep(idx, {
                    button: { ...cs.list[idx]?.button, title: v },
                  })} />
              </SplitRow>
            </>
          )}
        </React.Fragment>
      ))}

      <ImageUploadField
        label="Upload image"
        currentSrc={cs.imageSrc || undefined}
        onFile={async f => {
          try {
            const formData = new FormData();
            formData.append('file',        f);
            formData.append('type',        'IMAGE');
            formData.append('filename',    f.name);
            formData.append('alt',         '');
            formData.append('description', '');

            const res           = await ApiPost(`${API_ENDPOINTS.UPLOAD_MEDIA}?userUuid=${userId}`, formData);
            const uploadedUrl:  string | undefined = (res as any)?.data?.data?.url;
            const uploadedUuid: string | undefined = (res as any)?.data?.data?.uuid;

            if (uploadedUrl) {
              setCs({ imageSrc: uploadedUrl });
              if (uploadedUuid) registerMediaUsage(uploadedUuid);
              return uploadedUrl;
            }
          } catch (err) {
            console.error('[JoinUsImgUpload] upload failed:', err);
          }
          return undefined;
        }}
        onMediaClick={
          onMediaClick
            ? () => onMediaClick(item => { if (item) setCs({ imageSrc: item.src }); })
            : undefined
        }
      />
    </>
  );
};

export default JoinUsEditor;